import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, PageHeader, EmptyState, Badge } from "@/components/kit";
import { TID } from "@/constants/testIds";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";

export default function CustomersPage() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    const [c, b] = await Promise.all([
      api.get(`/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`),
      api.get("/branches"),
    ]);
    setCustomers(c.data);
    setBranches(b.data);
    if (user.role !== "agent") {
      try {
        const u = await api.get("/users");
        setAgents(u.data.filter((x) => x.role === "agent"));
      } catch { /* not permitted */ }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const canCreate = ["super_admin", "branch_admin", "agent", "staff"].includes(user.role);

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <PageHeader
        title="Customers"
        subtitle="Manage members enrolled in the cooperative."
        action={
          canCreate && (
            <Link
              to="/customers/new"
              data-testid={TID.addBtn("customer")}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-emerald-700 text-white hover:bg-emerald-800 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1"
            >
              <Plus size={16} weight="bold" /> Add customer
            </Link>
          )
        }
      />

      <Card className="p-4 mb-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="customer-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or phone…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
          />
        </div>
      </Card>

      <Card>
        {customers.length === 0 ? (
          <EmptyState title="No customers yet" subtitle="Add your first customer to get started." />
        ) : (
          <>
            {/* Mobile: card list */}
            <div className="md:hidden divide-y divide-slate-100">
              {customers.map((c) => {
                const branch = branches.find((b) => b.id === c.branch_id);
                const kinds = c.kyc_kinds || [];
                return (
                  <Link
                    key={c.id}
                    to={`/customers/${c.id}`}
                    data-testid={`customer-row-${c.id}`}
                    className="flex items-center gap-3 p-4 hover:bg-slate-50 active:bg-slate-100"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-semibold shrink-0">
                      {c.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 mono truncate">{c.phone}</div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {c.customer_code && (
                          <span className="text-[10px] mono text-slate-400">{c.customer_code}</span>
                        )}
                        {c.kyc_complete ? (
                          <Badge tone="success" data-testid={`kyc-complete-${c.id}`}>KYC ✓</Badge>
                        ) : kinds.length === 0 ? (
                          <Badge tone="danger" data-testid={`kyc-missing-${c.id}`}>No KYC</Badge>
                        ) : (
                          <Badge tone="warning" data-testid={`kyc-partial-${c.id}`}>{kinds.length}/3</Badge>
                        )}
                        {branch && <Badge tone="info">{branch.code}</Badge>}
                      </div>
                    </div>
                    <span className="text-slate-300 shrink-0">›</span>
                  </Link>
                );
              })}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Name</th>
                    <th className="text-left px-4 py-3 font-semibold">Phone</th>
                    <th className="text-left px-4 py-3 font-semibold">KYC</th>
                    <th className="text-left px-4 py-3 font-semibold">Branch</th>
                    <th className="text-left px-4 py-3 font-semibold">Agent</th>
                    <th className="text-right px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => {
                    const branch = branches.find((b) => b.id === c.branch_id);
                    const agent = agents.find((a) => a.id === c.agent_id);
                    const kinds = c.kyc_kinds || [];
                    return (
                      <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${c.id}-desktop`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{c.name}</div>
                          <div className="text-xs text-slate-500">{c.email || "—"}</div>
                        </td>
                        <td className="px-4 py-3 mono">{c.phone}</td>
                        <td className="px-4 py-3">
                          {c.kyc_complete ? (
                            <Badge tone="success">KYC complete</Badge>
                          ) : kinds.length === 0 ? (
                            <Badge tone="danger">No documents</Badge>
                          ) : (
                            <Badge tone="warning">{kinds.length}/3 uploaded</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">{branch ? <Badge tone="info">{branch.code}</Badge> : "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{agent?.name || "—"}</td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/customers/${c.id}`}
                            data-testid={`view-customer-${c.id}`}
                            className="text-emerald-700 font-medium text-sm hover:underline"
                          >
                            View →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
